import CheckCircleOutlineOutlined from "@mui/icons-material/CheckCircleOutlineOutlined";
import SyncDisabledOutlined from "@mui/icons-material/SyncDisabledOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import WorkOffOutlined from "@mui/icons-material/WorkOffOutlined";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../../../components/InHouseTooltip/InHouseTooltip";
import { type HrisEmployeeShiftStats, type HrisRawEmployeeRow, HrisRawSyncStatus, type JobNotEnabledEmployee } from "../../../../../../utils/models";

/**
 * Per-employee status icon (DAT-154): blocked (never synced), jobNotEnabled (synced, active, but the
 * department doesn't enable their job — DAT-834), deactivated (nurse no longer active), or syncing.
 * Its tooltip also carries the 30-day shift counts.
 */
export const EmployeeSyncStatusIcon: FC<{
	emp: HrisRawEmployeeRow;
	stats?: HrisEmployeeShiftStats;
	/** Set when this employee's primary job isn't enabled for the department. Read-only signal. */
	jobNotEnabled?: JobNotEnabledEmployee;
}> = ({ emp, stats, jobNotEnabled }) => {
	const { t } = useTranslation();
	const common = { fontSize: "small" as const, "data-testid": `hris-mapping-employee-sync-status-${emp.employeeId}` };
	if (emp.syncStatus !== HrisRawSyncStatus.Synced) {
		return (
			<InHouseTooltip
				title={t("hris.mapping.rawEmployees.unsyncedTooltip", {
					status: emp.syncStatus,
					detail: emp.rejectionDetail ?? "",
					updatedAt: DateTime.fromISO(emp.updatedAt).toLocaleString(DateTime.DATE_MED),
				})}
			>
				<WarningAmberOutlined color="warning" {...common} data-employee-sync-state="blocked" />
			</InHouseTooltip>
		);
	}
	const shiftCounts = t("hris.mapping.rawEmployees.shiftCountsTooltip", {
		actual: stats?.actualShiftsLast30 ?? 0,
		scheduled: stats?.scheduledShiftsLast30 ?? 0,
	});
	// Ranked under "never synced": a row that isn't syncing at all has a more urgent problem, and the
	// mismatch here is about the nurse the sync PRODUCED. Above "syncing", because a green check on a
	// misconfigured employee is exactly the false all-clear this signal exists to remove. Can't collide
	// with `deactivated` — the endpoint only reports employees active in this department.
	if (jobNotEnabled) {
		return (
			<InHouseTooltip
				title={
					<>
						{t("hris.mapping.rawEmployees.employeeStatus.jobNotEnabled", {
							job: jobNotEnabled.jobName ?? t("department.config.shifts.job.unknown"),
						})}
						<br />
						{t("hris.mapping.rawEmployees.jobNotEnabledFix")}
						<br />
						{shiftCounts}
					</>
				}
			>
				<WorkOffOutlined color="warning" {...common} data-employee-sync-state="job-not-enabled" />
			</InHouseTooltip>
		);
	}
	// A missing stats row means the employee isn't linked to a nurse (the stats endpoint only returns
	// linked employees), so it's not actively syncing — match countSyncingEmployees and don't default to active.
	const active = stats?.active === true;
	const title = (
		<>
			{t(active ? "hris.mapping.rawEmployees.employeeStatus.syncing" : "hris.mapping.rawEmployees.employeeStatus.deactivated")}
			<br />
			{shiftCounts}
		</>
	);
	return (
		<InHouseTooltip title={title}>
			{active ? (
				<CheckCircleOutlineOutlined color="success" {...common} data-employee-sync-state="syncing" />
			) : (
				<SyncDisabledOutlined color="disabled" {...common} data-employee-sync-state="deactivated" />
			)}
		</InHouseTooltip>
	);
};
